<template>
  <div class="finance-todo-list">
    <aside class="category-panel">
      <button
        type="button"
        class="category-card"
        :class="{ active: activeCategory === 'negativeProfitOrder' }"
        @click="switchCategory('negativeProfitOrder')"
      >
        <div class="category-name">
          <span class="name-text">负毛利订单</span>
          <span class="count-badge">{{ pendingCount }}</span>
        </div>
      </button>
      <button
        type="button"
        class="category-card"
        :class="{ active: activeCategory === 'payableUnverifiedOrder' }"
        @click="switchCategory('payableUnverifiedOrder')"
      >
        <div class="category-name">
          <span class="name-text">应付费用未核销</span>
        </div>
      </button>
    </aside>

    <section class="todo-content">
      <template v-if="activeCategory === 'negativeProfitOrder'">
        <div class="filter-bar">
          <el-input v-model.trim="negativeFilter.batchNo" placeholder="批次号" clearable class="filter-item" @keyup.enter.native="search" />
          <el-input v-model.trim="negativeFilter.jobNo" placeholder="订单号" clearable class="filter-item" @keyup.enter.native="search" />
          <el-input v-model.trim="negativeFilter.customerName" placeholder="客户" clearable class="filter-item" @keyup.enter.native="search" />
          <el-radio-group v-model="negativeFilter.handleStatus" class="status-group" @change="search">
            <el-radio-button label="">全部</el-radio-button>
            <el-radio-button :label="0">未处理</el-radio-button>
            <el-radio-button :label="1">已处理</el-radio-button>
          </el-radio-group>
          <el-button type="primary" icon="el-icon-search" @click="search">搜索</el-button>
          <el-button icon="el-icon-refresh-left" @click="reset">重置</el-button>
          <el-button v-if="$store.getters.isAdmin" icon="el-icon-refresh" @click="manualGenerate">手动补跑</el-button>
        </div>
        <div ref="tableWrapper" class="table-wrapper">
          <el-table
            ref="orderTable"
            v-loading="loading"
            :data="negativeList"
            row-key="uuid"
            class="order-table"
            :max-height="tableMaxHeight"
            highlight-current-row
            :header-cell-style="{ background: '#f5f7fa', color: '#303133' }"
          >
            <el-table-column label="订单号" min-width="210">
              <template slot-scope="{ row }">
                <div class="job-cell">
                  <el-button type="text" class="job-no" @click="openOrder(row)">{{ row.jobNo }}</el-button>
                  <el-tooltip content="复制订单号" placement="top">
                    <el-button type="text" icon="el-icon-document-copy" class="copy-job-no" @click.stop="copyJobNo(row.jobNo, $event)" />
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="batchNo" label="批次号" min-width="130" show-overflow-tooltip />
            <el-table-column prop="customerName" label="客户" min-width="190" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.customerName || '-' }}</template>
            </el-table-column>
            <el-table-column label="ETD" width="105">
              <template slot-scope="{ row }">{{ formatDate(row.etd) }}</template>
            </el-table-column>
            <el-table-column label="业务类型" width="95">
              <template slot-scope="{ row }">{{ formatBusinessType(row.businessType) }}</template>
            </el-table-column>
            <el-table-column label="毛利（本位币）" min-width="145" align="right">
              <template slot-scope="{ row }"><span class="profit">{{ formatAmount(row.grossProfit) }}</span></template>
            </el-table-column>
            <el-table-column label="销售" width="110" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.salesName || '-' }}</template>
            </el-table-column>
            <el-table-column label="客服" width="110" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.csrName || '-' }}</template>
            </el-table-column>
            <el-table-column label="处理人" width="150">
              <template slot-scope="{ row }">
                <div v-if="row.handleHistories && row.handleHistories.length" class="handler-history">
                  <div v-for="history in row.handleHistories" :key="history.uuid" class="handler-item">
                    <div class="handler-name">{{ history.operatorName || '-' }}</div>
                    <div class="handler-time">{{ formatDateTime(history.operateTime) }}</div>
                  </div>
                </div>
                <div v-else-if="row.handleStatus === 1" class="handler">
                  <div class="handler-name">{{ row.handlerName || '-' }}</div>
                  <div class="handler-time">{{ formatDateTime(row.handleTime) }}</div>
                </div>
                <span v-else class="pending">待处理</span>
              </template>
            </el-table-column>
            <el-table-column prop="reason" label="原因说明" min-width="200" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <span v-if="row.reason">{{ row.reason }}</span>
                <span v-else class="reason-empty">未处理</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" @click="openReasonDialog(row)">{{ row.handleStatus === 1 ? '修改备注' : '备注' }}</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </template>

      <template v-else>
        <div class="filter-bar">
          <el-input v-model.trim="payableFilter.batchNo" placeholder="批次号" clearable class="filter-item" @keyup.enter.native="search" />
          <el-input v-model.trim="payableFilter.jobNo" placeholder="订单号" clearable class="filter-item" @keyup.enter.native="search" />
          <el-input v-model.trim="payableFilter.customerName" placeholder="客户" clearable class="filter-item" @keyup.enter.native="search" />
          <el-radio-group v-model="payableFilter.chargeNature" class="status-group" @change="search">
            <el-radio-button label="">全部</el-radio-button>
            <el-radio-button label="SPECIAL_PAYABLE">专项应付</el-radio-button>
            <el-radio-button label="OTHER_PAYABLE">其他应付</el-radio-button>
          </el-radio-group>
          <el-radio-group v-model="payableFilter.handleStatus" class="status-group" @change="search">
            <el-radio-button label="">全部</el-radio-button>
            <el-radio-button :label="0">未处理</el-radio-button>
            <el-radio-button :label="1">已处理</el-radio-button>
          </el-radio-group>
          <el-button type="primary" icon="el-icon-search" @click="search">搜索</el-button>
          <el-button icon="el-icon-refresh-left" @click="reset">重置</el-button>
          <el-button v-if="$store.getters.isAdmin" icon="el-icon-refresh" @click="manualGenerate">手动补跑</el-button>
        </div>
        <div ref="tableWrapper" class="table-wrapper">
          <el-table
            ref="orderTable"
            v-loading="loading"
            :data="payableList"
            row-key="uuid"
            class="order-table"
            :max-height="tableMaxHeight"
            highlight-current-row
            :header-cell-style="{ background: '#f5f7fa', color: '#303133' }"
          >
            <el-table-column label="订单号" min-width="195">
              <template slot-scope="{ row }">
                <div class="job-cell">
                  <el-button type="text" class="job-no" @click="openOrder(row)">{{ row.jobNo }}</el-button>
                  <el-tooltip content="复制订单号" placement="top">
                    <el-button type="text" icon="el-icon-document-copy" class="copy-job-no" @click.stop="copyJobNo(row.jobNo, $event)" />
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="batchNo" label="批次号" min-width="120" show-overflow-tooltip />
            <el-table-column prop="customerName" label="客户" min-width="170" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.customerName || '-' }}</template>
            </el-table-column>
            <el-table-column label="ETD" width="100">
              <template slot-scope="{ row }">{{ formatDate(row.etd) }}</template>
            </el-table-column>
            <el-table-column label="业务类型" width="90">
              <template slot-scope="{ row }">{{ formatBusinessType(row.businessType) }}</template>
            </el-table-column>
            <el-table-column label="费用性质" width="105">
              <template slot-scope="{ row }">
                <el-tag size="small" :type="row.chargeNature === 'SPECIAL_PAYABLE' ? 'warning' : ''">
                  {{ formatChargeNature(row.chargeNature) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="应付合计" min-width="120" align="right">
              <template slot-scope="{ row }">{{ formatAmount(row.payableAmount) }}</template>
            </el-table-column>
            <el-table-column label="未核销余额" min-width="125" align="right">
              <template slot-scope="{ row }"><span class="profit">{{ formatUnverifiedAmount(row.unverifiedAmount) }}</span></template>
            </el-table-column>
            <el-table-column label="推送对象" min-width="145" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ formatPushUsers(row) }}</template>
            </el-table-column>
            <el-table-column label="处理人" width="145">
              <template slot-scope="{ row }">
                <div v-if="row.handleHistories && row.handleHistories.length" class="handler-history">
                  <div v-for="history in row.handleHistories" :key="history.uuid" class="handler-item">
                    <div class="handler-name">{{ history.operatorName || '-' }}</div>
                    <div class="handler-time">{{ formatDateTime(history.operateTime) }}</div>
                  </div>
                </div>
                <div v-else-if="row.handleStatus === 1" class="handler">
                  <div class="handler-name">{{ row.handlerName || '-' }}</div>
                  <div class="handler-time">{{ formatDateTime(row.handleTime) }}</div>
                </div>
                <span v-else class="pending">待处理</span>
              </template>
            </el-table-column>
            <el-table-column prop="reason" label="原因说明" min-width="185" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <span v-if="row.reason">{{ row.reason }}</span>
                <span v-else class="reason-empty">未处理</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="95" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" @click="openReasonDialog(row)">{{ row.handleStatus === 1 ? '修改备注' : '备注' }}</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </template>

      <div ref="paginationWrapper" class="pagination-wrapper">
        <cargo-pagination
          :total="currentFilter.total"
          :page.sync="currentFilter.pageNum"
          :limit.sync="currentFilter.pageSize"
          @pagination="query"
        />
      </div>
    </section>

    <el-dialog
      :visible.sync="reasonDialog.visible"
      :title="reasonDialog.title"
      width="700px"
      :close-on-click-modal="false"
    >
      <div class="dialog-summary">
        <div class="summary-row">
          <span>批次号：<b>{{ reasonDialog.batchNo }}</b></span>
          <span>订单号：<b class="job-no">{{ reasonDialog.jobNo }}</b></span>
          <span>客户：{{ reasonDialog.customerName || '-' }}</span>
        </div>
        <div v-if="reasonDialog.category === 'negativeProfitOrder'" class="summary-row">
          <span>ETD：{{ formatDate(reasonDialog.etd) }}</span>
          <span>业务类型：{{ formatBusinessType(reasonDialog.businessType) }}</span>
          <span>销售：{{ reasonDialog.salesName || '-' }}</span>
          <span>客服：{{ reasonDialog.csrName || '-' }}</span>
          <span>毛利：<b class="profit">{{ formatAmount(reasonDialog.grossProfit) }}</b></span>
        </div>
        <div v-else class="summary-row">
          <span>ETD：{{ formatDate(reasonDialog.etd) }}</span>
          <span>业务类型：{{ formatBusinessType(reasonDialog.businessType) }}</span>
          <span>费用性质：{{ formatChargeNature(reasonDialog.chargeNature) }}</span>
          <span>应付合计：{{ formatAmount(reasonDialog.payableAmount) }}</span>
          <span>未核销余额：<b class="profit">{{ formatUnverifiedAmount(reasonDialog.unverifiedAmount) }}</b></span>
        </div>
      </div>
      <el-form ref="reasonForm" :model="reasonDialog" :rules="reasonRules" label-position="top">
        <el-form-item label="原因说明" prop="reason">
          <el-input
            v-model.trim="reasonDialog.reason"
            type="textarea"
            :rows="5"
            maxlength="1000"
            show-word-limit
            placeholder="请输入原因说明，如：供应商账期未到 / 费用争议待审 / 付款申请在途..."
          />
        </el-form-item>
      </el-form>
      <div class="dialog-hint">提交后该对象待办自动完成（所有接收人名下同时消失）；已处理对象可修改，记录最新操作人。</div>
      <div slot="footer">
        <el-button @click="reasonDialog.visible = false">取 消</el-button>
        <el-button type="primary" :loading="reasonDialog.submitting" @click="submitReason">提 交</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import handleClipboard from '@/utils/clipboard'
import {
  handleNegativeProfitOrderFunc,
  manualGenerateNegativeProfitOrderFunc,
  queryNegativeProfitOrderFunc
} from '@/api/financeManage/negativeProfitOrder'
import {
  handlePayableUnverifiedOrderFunc,
  manualGeneratePayableUnverifiedOrderFunc,
  queryPayableUnverifiedOrderFunc
} from '@/api/financeManage/payableUnverifiedOrder'

export default {
  name: 'NegativeProfitOrder',
  data() {
    return {
      loading: false,
      tableMaxHeight: 420,
      activeCategory: this.$route.query.tab === 'payableUnverifiedOrder'
        ? 'payableUnverifiedOrder'
        : 'negativeProfitOrder',
      negativeList: [],
      payableList: [],
      negativeFilter: this.buildFilter(),
      payableFilter: this.buildFilter(),
      reasonDialog: {
        visible: false,
        submitting: false,
        title: '备注原因',
        category: 'negativeProfitOrder',
        uuid: '',
        jobNo: '',
        batchNo: '',
        customerName: '',
        etd: '',
        businessType: '',
        salesName: '',
        csrName: '',
        chargeNature: '',
        plannerName: '',
        opName: '',
        grossProfit: 0,
        payableAmount: 0,
        unverifiedAmount: 0,
        reason: ''
      },
      reasonRules: {
        reason: [{ required: true, message: '原因说明为必填项', trigger: 'blur' }]
      }
    }
  },
  computed: {
    currentFilter() {
      return this.activeCategory === 'negativeProfitOrder'
        ? this.negativeFilter
        : this.payableFilter
    }
  },
  created() {
    const jobNo = this.$route.query.jobNo
    if (jobNo) {
      if (this.activeCategory === 'payableUnverifiedOrder') {
        this.payableFilter.jobNo = jobNo
      } else {
        this.negativeFilter.jobNo = jobNo
      }
    }
    this.query()
  },
  mounted() {
    this.$nextTick(this.updateTableHeight)
    this.resizeObserver = new ResizeObserver(this.updateTableHeight)
    this.resizeObserver.observe(this.$refs.tableWrapper)
    window.addEventListener('resize', this.updateTableHeight)
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    window.removeEventListener('resize', this.updateTableHeight)
  },
  methods: {
    buildFilter() {
      return {
        batchNo: '',
        jobNo: '',
        customerName: '',
        chargeNature: '',
        handleStatus: '',
        branchId: this.$store.getters.currentOfficeId || 0,
        pageNum: 1,
        pageSize: 20,
        total: 0
      }
    },
    switchCategory(category) {
      if (this.activeCategory === category) {
        return
      }
      this.activeCategory = category
      this.$router.replace({ query: { ...this.$route.query, tab: category }})
      this.$nextTick(() => {
        this.updateTableHeight()
        this.query()
      })
    },
    updateTableHeight() {
      const wrapper = this.$refs.tableWrapper
      const pagination = this.$refs.paginationWrapper
      if (wrapper) {
        const wrapperTop = wrapper.getBoundingClientRect().top
        const paginationHeight = pagination ? pagination.offsetHeight + 4 : 16
        this.tableMaxHeight = Math.max(
          240,
          Math.floor(window.innerHeight - wrapperTop - paginationHeight - 16)
        )
      }
    },
    buildQueryParams(filter) {
      return {
        ...filter,
        handleStatus: filter.handleStatus === '' ? null : filter.handleStatus,
        chargeNature: filter.chargeNature || null
      }
    },
    async query() {
      this.loading = true
      try {
        if (this.activeCategory === 'negativeProfitOrder') {
          const res = await queryNegativeProfitOrderFunc(this.buildQueryParams(this.negativeFilter))
          const pageInfo = res.data || {}
          this.negativeList = pageInfo.list || []
          this.negativeFilter.total = Number(pageInfo.total || 0)
        } else {
          const res = await queryPayableUnverifiedOrderFunc(this.buildQueryParams(this.payableFilter))
          const pageInfo = res.data || {}
          this.payableList = pageInfo.list || []
          this.payableFilter.total = Number(pageInfo.total || 0)
        }
        if (this.currentFilter.jobNo) {
          this.$nextTick(this.highlightJobNo)
        }
      } finally {
        this.loading = false
      }
    },
    search() {
      this.currentFilter.pageNum = 1
      this.query()
    },
    reset() {
      const branchId = this.currentFilter.branchId
      const pageSize = this.currentFilter.pageSize
      Object.assign(this.currentFilter, {
        batchNo: '',
        jobNo: '',
        customerName: '',
        chargeNature: '',
        handleStatus: '',
        branchId,
        pageNum: 1,
        pageSize
      })
      this.search()
    },
    highlightJobNo() {
      const list = this.activeCategory === 'negativeProfitOrder' ? this.negativeList : this.payableList
      const row = list.find(item => item.jobNo === this.currentFilter.jobNo)
      if (row && this.$refs.orderTable) {
        this.$refs.orderTable.setCurrentRow(row)
      }
    },
    openReasonDialog(row) {
      const fields = ['uuid', 'jobNo', 'batchNo', 'customerName', 'etd', 'businessType']
      fields.forEach(field => {
        this.reasonDialog[field] = row[field]
      })
      this.reasonDialog.category = this.activeCategory
      this.reasonDialog.salesName = row.salesName || ''
      this.reasonDialog.csrName = row.csrName || ''
      this.reasonDialog.chargeNature = row.chargeNature || ''
      this.reasonDialog.plannerName = row.plannerName || ''
      this.reasonDialog.opName = row.opName || ''
      this.reasonDialog.grossProfit = row.grossProfit || 0
      this.reasonDialog.payableAmount = row.payableAmount || 0
      this.reasonDialog.unverifiedAmount = row.unverifiedAmount || 0
      this.reasonDialog.reason = row.reason || ''
      this.reasonDialog.title = row.handleStatus === 1 ? '修改备注' : '备注原因'
      this.reasonDialog.visible = true
      this.$nextTick(() => {
        if (this.$refs.reasonForm) {
          this.$refs.reasonForm.clearValidate()
        }
      })
    },
    copyJobNo(jobNo, event) {
      handleClipboard(jobNo, event)
    },
    submitReason() {
      this.$refs.reasonForm.validate(async valid => {
        if (!valid) {
          return
        }
        this.reasonDialog.submitting = true
        try {
          const body = { uuid: this.reasonDialog.uuid, reason: this.reasonDialog.reason }
          if (this.reasonDialog.category === 'negativeProfitOrder') {
            await handleNegativeProfitOrderFunc(body)
          } else {
            await handlePayableUnverifiedOrderFunc(body)
          }
          this.$message.success('保存成功，待办已完成')
          this.reasonDialog.visible = false
          this.query()
        } finally {
          this.reasonDialog.submitting = false
        }
      })
    },
    async manualGenerate() {
      this.loading = true
      try {
        if (this.activeCategory === 'negativeProfitOrder') {
          await manualGenerateNegativeProfitOrderFunc()
        } else {
          await manualGeneratePayableUnverifiedOrderFunc()
        }
        this.$message.success('补跑完成')
        this.query()
      } finally {
        this.loading = false
      }
    },
    openOrder(row) {
      const routeNameMap = { AE: 'AeBooking', AI: 'AiBooking' }
      const routeName = routeNameMap[row.businessType] || 'SeaBooking'
      this.$router.push({
        name: routeName,
        params: { jobUuid: row.jobUuid, activeTabName: 'tabCharges' }
      })
    },
    formatDate(value) {
      return value ? moment(value).format('YYYY-MM-DD') : '-'
    },
    formatDateTime(value) {
      return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    formatAmount(value) {
      const amount = Number(value || 0)
      return amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatUnverifiedAmount(value) {
      const amount = Number(value || 0)
      return amount > 0 ? `-${this.formatAmount(amount)}` : this.formatAmount(amount)
    },
    formatBusinessType(value) {
      const typeMap = {
        SE: '海运',
        AE: '空运',
        SI: '海运进口',
        AI: '空运进口',
        RE: '铁路出口',
        RI: '铁路进口'
      }
      return typeMap[value] || value || '-'
    },
    formatChargeNature(value) {
      const typeMap = { SPECIAL_PAYABLE: '专项应付', OTHER_PAYABLE: '其他应付' }
      return typeMap[value] || value || '-'
    },
    formatPushUsers(row) {
      if (row.chargeNature === 'SPECIAL_PAYABLE') {
        return [row.salesName, row.csrName].filter(Boolean).join('、') || '-'
      }
      return [row.plannerName, row.opName].filter(Boolean).join('、') || '-'
    }
  }
}
</script>

<style scoped lang="scss">
.finance-todo-list {
  padding: 12px 16px 16px;
  height: calc(100vh - 84px);
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  gap: 14px;

  .category-panel {
    flex: 0 0 180px;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .category-card {
      padding: 12px 14px;
      border: 1px solid #e4e9f2;
      border-left: 3px solid transparent;
      border-radius: 4px;
      background: #fff;
      cursor: pointer;
      text-align: left;

      &.active {
        border-color: #c8dcff;
        border-left-color: #2f6bff;
        background: #f4f8ff;
        color: #1f4fb8;
      }

      .category-name {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;

        .name-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  .todo-content {
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .filter-bar {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;

      .filter-item {
        width: 165px;
      }

      .status-group {
        margin-right: 4px;
      }
    }

    .table-wrapper {
      border: 1px solid #e8edf5;
      border-radius: 4px;
    }

    .pagination-wrapper {
      display: flex;
      justify-content: flex-end;
    }

    .job-cell {
      display: flex;
      align-items: center;

      .job-no {
        max-width: 165px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .profit {
      color: #f56c6c;
      font-weight: 600;
    }

    .reason-empty {
      color: #909399;
    }

    .pending {
      color: #e6a23c;
    }

    .handler-history {
      line-height: 18px;
    }

    .handler-name,
    .handler-time {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .handler-time {
      color: #909399;
      font-size: 12px;
    }
  }

  .dialog-summary {
    margin-bottom: 14px;
    padding: 10px 14px;
    border-radius: 4px;
    background: #f5f7fa;

    .summary-row {
      display: flex;
      flex-wrap: wrap;
      gap: 10px 18px;
      color: #606266;
      line-height: 22px;
    }
  }

  .dialog-hint {
    color: #909399;
    line-height: 20px;
  }
}
</style>
